import {
  BellAlertIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  CalendarDaysIcon,
  CubeIcon,
  IdentificationIcon,
  KeyIcon,
  UserGroupIcon,
  BanknotesIcon,
  ChartBarIcon,
  RectangleGroupIcon,
  SparklesIcon,
  UsersIcon,
  ShieldCheckIcon,
  StarIcon,
  GiftIcon,
} from "@heroicons/react/24/outline";

import { permissionFor } from "../services/routePermissions";

/**
 * `title` is a translation key, resolved in the Sidebar.
 *
 * `permission` hides an entry from anyone whose role does not carry it. That is
 * a convenience only — the API checks every action again — so a missing entry
 * never stands in for access control.
 *
 * It is not written out here: `permissionFor(path)` reads the one route map
 * the router guards with, so an entry and the page behind it always ask for
 * the same catalogue permission. An entry whose path is absent from that map
 * needs none — the dashboard, the business page and the security page are
 * open to every signed-in user, and the server gates none of them either.
 */
export const BUSINESS_MENU = [
  {
    key: "dashboard",
    title: "menu.dashboard",
    icon: RectangleGroupIcon,
    path: "/",
    exact: true,
  },
  {
    key: "myBusiness",
    title: "menu.myBusiness",
    icon: BuildingOffice2Icon,
    path: "/business",
    exact: true,
  },
  {
    // The diary sits high: it is the screen reception lives in all day.
    key: "diary",
    title: "menu.diary",
    icon: CalendarDaysIcon,
    path: "/diary",
    exact: true,
    permission: permissionFor("/diary"),
  },
  {
    key: "branches",
    title: "menu.branches",
    icon: BuildingStorefrontIcon,
    // A collapsible group; `defaultOpen` expands it on first paint.
    defaultOpen: true,
    subMenu: [
      { key: "allBranches", title: "menu.allBranches", path: "/branches", exact: true, permission: permissionFor("/branches") },
      { key: "newBranch", title: "menu.newBranch", path: "/branches/create", permission: permissionFor("/branches/create") },
    ],
  },
  {
    key: "staff",
    title: "menu.staff",
    icon: UsersIcon,
    path: "/staff",
    exact: true,
    permission: permissionFor("/staff"),
  },
  {
    // The people who perform services. Separate from Staff above, which is who
    // may sign in — most providers have no login at all.
    key: "providers",
    title: "menu.providers",
    icon: IdentificationIcon,
    path: "/providers",
    exact: true,
    permission: permissionFor("/providers"),
  },
  {
    key: "services",
    title: "menu.services",
    icon: SparklesIcon,
    // Groups first, then the catalogue that fills them — a service has to
    // belong to a group, so that is the order the work is done in.
    subMenu: [
      { key: "serviceCategories", title: "menu.serviceCategories", path: "/service-categories", permission: permissionFor("/service-categories") },
      { key: "serviceCatalogue", title: "menu.serviceCatalogue", path: "/services", exact: true, permission: permissionFor("/services") },
    ],
  },
  {
    // Was "Catalog". Taxonomy and products are both business-wide, like
    // services: `allProducts` is the catalogue, and each product's Branches
    // action chooses where it is sold.
    key: "products",
    title: "menu.products",
    icon: CubeIcon,
    subMenu: [
      { key: "productCategories", title: "menu.productCategories", path: "/product-categories", permission: permissionFor("/product-categories") },
      { key: "brands", title: "menu.brands", path: "/brands", permission: permissionFor("/brands") },
      { key: "allProducts", title: "menu.allProducts", path: "/products", exact: true, permission: permissionFor("/products") },
    ],
  },
  {
    // The people the business serves, as opposed to the people who work there.
    key: "clients",
    title: "menu.clients",
    icon: UserGroupIcon,
    path: "/clients",
    exact: true,
    permission: permissionFor("/clients"),
  },
  {
    // BIZ-19. A daily screen for whoever cashes up, not a configuration one.
    key: "transactions",
    title: "menu.transactions",
    icon: BanknotesIcon,
    path: "/transactions",
    exact: true,
    permission: permissionFor("/transactions"),
  },
  {
    // Loyalty: the desk's Redeem first (used all day), then the members, then
    // the two configuration screens. `feature` hides the whole group when the
    // plan leaves loyalty out — the Sidebar shows an upgrade hint instead.
    key: "loyalty",
    title: "menu.loyalty",
    icon: GiftIcon,
    feature: "loyalty",
    subMenu: [
      { key: "loyaltyRedeem", title: "menu.loyaltyRedeem", path: "/loyalty/redeem", permission: permissionFor("/loyalty/redeem") },
      { key: "loyaltyMembers", title: "menu.loyaltyMembers", path: "/loyalty/members", permission: permissionFor("/loyalty/members") },
      { key: "loyaltyRewards", title: "menu.loyaltyRewards", path: "/loyalty/rewards", exact: true, permission: permissionFor("/loyalty/rewards") },
      { key: "loyaltyProgram", title: "menu.loyaltyProgram", path: "/loyalty/programs", permission: permissionFor("/loyalty/programs") },
    ],
  },
  {
    // Managed alerts (spec 08): what needs a person, routed by the server.
    key: "alerts",
    title: "menu.alerts",
    icon: BellAlertIcon,
    path: "/alerts",
    exact: true,
    permission: permissionFor("/alerts"),
  },
  {
    // What customers said, per branch. Read-only; hiding is the admin's.
    key: "reviews",
    title: "menu.reviews",
    icon: StarIcon,
    path: "/reviews",
    exact: true,
    permission: permissionFor("/reviews"),
  },
  {
    key: "roles",
    title: "menu.roles",
    icon: KeyIcon,
    path: "/roles",
    exact: true,
    permission: permissionFor("/roles"),
  },
  {
    key: "security",
    title: "menu.security",
    icon: ShieldCheckIcon,
    path: "/security",
    exact: true,
  },
  {
    // BIZ-20. The Menu sheet names twelve entries and Reports is not among
    // them, but OUP-1 says to reorder rather than remove — so it keeps its
    // place in the product and sits after the twelve rather than inside them.
    key: "reports",
    title: "menu.reports",
    icon: ChartBarIcon,
    path: "/reports",
    permission: permissionFor("/reports"),
  },
];
